import { useState, useEffect, useRef } from 'react'
import {
  FlexGrid,
  Typography,
  Card,
  QuantitySelector,
  Tooltip,
  Divider,
  Spacer,
  ChevronLink,
  Button,
} from '@telus-uds/components-web'
import { BlockRenderer } from '@/siteBuilder/renderer/contentful/BlockRenderer'
import { RichTextProps } from '../../core/RichText/RichText'
import { LinkProps } from '../../core/Link/Link'
import SavingsCard from './SavingsCard'
import { CalcValue } from './SavingsCalculator'
import { SavingsCalcImage, CardContainer, CtaContainer, DeviceValueContainer, DeviceInfoContainer } from './Styles'
import { HeadingProps } from '../../core/Heading/Heading'
import { ImageProps } from '../../core/Image/Image'
import { ButtonLinkProps } from '../../core/ButtonLink/ButtonLink'
import alloyEvents from '@/siteBuilder/utils/alloyAnalytics/savingsCalculator'

type DeviceCardProps = {
  deviceHeader: { fields: RichTextProps }
  savingsHeader: { fields: HeadingProps }
  savingsRateText: string
  restartButtonText: string
  homeImage: ImageProps
  homeValueLabel: string
  deviceValueLabel: string
  deviceValues: CalcValue[]
  rewardsValues?: CalcValue
  rewardLink?: LinkProps
  setCardView: (val: string) => void
  homeSavings: number
  province: string
  isMobile: boolean
  isTablet: boolean
  step2CTA: string
  step3CTAProps: ButtonLinkProps
  step3SubHeading: string
}

const DeviceCard = ({
  deviceHeader,
  savingsHeader,
  savingsRateText,
  restartButtonText,
  homeImage,
  homeValueLabel,
  deviceValueLabel,
  deviceValues,
  rewardsValues,
  rewardLink,
  setCardView,
  homeSavings,
  province,
  isMobile,
  isTablet,
  step2CTA,
  step3CTAProps,
  step3SubHeading,
}: DeviceCardProps) => {
  const [deviceSavings, setDeviceSavings] = useState<number>(0)
  const [savings, setSavings] = useState<Record<string, number>>({})
  const deviceSavingsCardRef = useRef<HTMLDivElement>(null)
  const deviceCardRef = useRef<HTMLDivElement>(null)
  const [updatedDeviceHeader, setUpdatedDeviceHeader] = useState(deviceHeader)
  const [counts, setCounts] = useState<Record<string, number>>(
    deviceValues.reduce((acc, device) => {
      acc[device.name] = 0
      return acc
    }, {} as Record<string, number>)
  )
  const [showSavingsCard, setShowSavingsCard] = useState(false)
  const [deviceImageHeight, setDeviceImageHeight] = useState<number>(0)

  useEffect(() => {
    const calculateHeight = () => {
      if (deviceCardRef.current) {
        setDeviceImageHeight(deviceCardRef.current.clientHeight)
      }
    }

    calculateHeight()

    const observer = new ResizeObserver(calculateHeight)
    if (deviceCardRef.current) {
      observer.observe(deviceCardRef.current)
    }

    if (!showSavingsCard) {
      setDeviceSavings(0)
      setSavings({})
      setCounts(
        deviceValues.reduce((acc, device) => {
          acc[device.name] = 0
          return acc
        }, {} as typeof counts)
      )
    }

    return () => {
      if (deviceCardRef.current) {
        observer.disconnect()
      }
    }
  }, [showSavingsCard])

  useEffect(() => {
    const newContent = deviceHeader.fields.richText.content.map((contentItem) => {
      return {
        ...contentItem,
        content: contentItem.content.map((content) => {
          if (content.nodeType === 'text' && content.value.includes('${savings}')) {
            return { ...content, value: content.value.replace('${savings}', homeSavings.toString()) }
          }
          return content
        }),
      }
    })

    setUpdatedDeviceHeader({
      ...deviceHeader,
      fields: {
        ...deviceHeader.fields,
        paragraphSize: isMobile ? null : deviceHeader.fields.paragraphSize,
        richText: {
          ...deviceHeader.fields.richText,
          content: newContent,
        },
      },
    })
  }, [homeSavings, isMobile])

  useEffect(() => {
    const sum = Object.values(savings).reduce((acc, curr) => acc + curr, 0)
    setDeviceSavings(sum)
  }, [savings])

  const updateSavings = (val: number, name: string, count: number) => {
    setSavings({ ...savings, [name]: val })
    setCounts({ ...counts, [name]: count })
  }

  const rewardsSavings: number = rewardsValues?.savingsValues[province]

  const totalSavingsValue: string = rewardsSavings
    ? (homeSavings + deviceSavings + rewardsSavings).toFixed(0).toString()
    : (homeSavings + deviceSavings).toFixed(0).toString()

  const renderDeviceValues = () => {
    return deviceValues.map((device, i) => {
      return (
        <div key={i}>
          {i > 0 && <Divider variant={{ decorative: true }} />}

          <DeviceValueContainer>
            <DeviceInfoContainer isMobile={isMobile}>
              {!isMobile && <BlockRenderer block={device.icon} />}
              <Spacer space={3} direction="row" />
              <Typography>
                {device.name}
                <Tooltip
                  //@ts-ignore
                  inline={true}
                  position="left"
                  content={device.tooltip}
                />
              </Typography>
            </DeviceInfoContainer>
            <div>
              <QuantitySelector
                variant={{ alternative: true }}
                minNumber={0}
                defaultValue={0}
                onChange={(value) => updateSavings(value * device.savingsValues[province], device.name, value)}
              />
              <Spacer space={2} />
            </div>
          </DeviceValueContainer>
        </div>
      )
    })
  }

  const renderCTAButton = () => {
    return (
      <Button
        variant={{ priority: 'high', width: 'responsive' }}
        onPress={() => {
          alloyEvents.toolComplete(counts, window.location.href, totalSavingsValue)
          setShowSavingsCard(true)
          deviceSavingsCardRef.current?.scrollIntoView({ behavior: 'smooth' })
        }}
      >
        {step2CTA}
      </Button>
    )
  }

  const renderBackButton = () => {
    return (
      <ChevronLink
        direction="left"
        onPress={() => {
          deviceSavingsCardRef.current?.scrollIntoView({ behavior: 'smooth' })
          setCardView('home')
        }}
      >
        {restartButtonText}
      </ChevronLink>
    )
  }

  const savingsCardProps = {
    savingsHeader,
    savingsRateText,
    homeImage,
    homeValueLabel,
    homeSavings,
    deviceValueLabel,
    deviceSavings,
    rewardsValueLabel: rewardsValues?.name,
    rewardsSavings,
    rewardLink,
    isMobile,
    deviceSavingsCardRef,
    restartButtonText,
    setShowSavingsCard,
    showSavingsCard,
    isTablet,
    step3CTAProps,
    totalSavingsValue,
    step3SubHeading,
  }

  return (
    <>
      {showSavingsCard ? (
        <SavingsCard {...savingsCardProps} />
      ) : (
        <FlexGrid.Row ref={deviceSavingsCardRef}>
          <FlexGrid.Col xs={12} lg={8}>
            <Card tokens={{ borderWidth: 0 }} variant={{ padding: 'custom' }} ref={deviceCardRef}>
              <CardContainer isMobile={!!isMobile}>
                {<BlockRenderer block={updatedDeviceHeader} />}
                {!isMobile && <Spacer space={2} />}
                {renderDeviceValues()}
                {isMobile ? (
                  <>
                    <Spacer space={4} />
                    {renderCTAButton()}
                    <Spacer space={4} />
                    {renderBackButton()}
                  </>
                ) : (
                  <>
                    <Spacer space={3} />
                    <CtaContainer justifyContent="space-between">
                      <CtaContainer>{renderBackButton()}</CtaContainer>
                      <CtaContainer>{renderCTAButton()}</CtaContainer>
                    </CtaContainer>
                  </>
                )}
              </CardContainer>
            </Card>
          </FlexGrid.Col>
          <FlexGrid.Col xs={0} lg={4}>
            <SavingsCalcImage height={deviceImageHeight}>
              <BlockRenderer block={homeImage} />
            </SavingsCalcImage>
          </FlexGrid.Col>
        </FlexGrid.Row>
      )}
    </>
  )
}

export default DeviceCard
